import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';

import FilterBar, {
  FilterField,
  FilterValues,
  toDateOnlyString,
  toFilterIdList
} from '../../components/Filter';
import EstateUnitFilter from '../../components/EstateUnitFilter';
import PopupModal from '../../components/PopupModal';
import { CustoDropDownItemProps } from '../../components/types';
import { iRootState } from '../../store';
import { dangerNotification } from '../../services/notification';
import {
  getBondInsuranceByID,
  getBondInsurances,
  sortBondInsurances
} from '../../services/bond-insurance';
import { useDropDownUsers } from '../../services/broker';
import { AvailableBrokersAndEstatesState } from '../../store/types/temp-types';
import { UsersToCustomDropDownOptions } from '../../util';
import { appendEstateUnitIdToFilter } from '../../utils/estateUnitFilter.util';
import { isEstateRole } from '../../utils/isEstateRole';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';
import { BannerLocationActivationEnum } from '../../types/Banner';
import { InsurerState } from '../../types/InsurerState';
import BondInsurancesTable from './BondInsurancesTable';
import {
  clientTypeOptions,
  FilterData,
  ListingRouteInfo,
  moreFiltersOptions,
  MoreFiltersEnum,
  selectableStatus,
  typeOptions
} from './types';

export type BondInsuranceListProps = {
  availableBrokersAndEstates: iRootState['availableBrokersAndEstates'];
  user: iRootState['user'];
  updateAvailableBrokersAndEstates: (
    availableBrokers: AvailableBrokersAndEstatesState
  ) => AvailableBrokersAndEstatesState | void;
} & RouteComponentProps<ListingRouteInfo>;

const SEARCH_FIELDS: Array<'document' | 'name' | 'address'> = [
  'document',
  'name',
  'address'
];

interface LocationState {
  expiresInAWeek?: boolean;
  bondInsuranceId?: string;
  bondInsuranceAnalysisId?: string;
  analysisStatus?: string;
}

type ApiFilters = FilterData & { estateUnitId?: string };

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  const dateOnly = toDateOnlyString(value);
  return dateOnly || undefined;
};

const buildInitialFilterValues = (
  locationState: LocationState | undefined,
  singleEstateId: string
): FilterValues => ({
  type: 'document',
  argument: '',
  insurer: [],
  estate_id: singleEstateId,
  start: '',
  end: '',
  analysisStatus: locationState?.analysisStatus
    ? [locationState.analysisStatus]
    : [],
  estateUnitId: '',
  accountManager: '',
  clientType: '',
  moreFilters: locationState?.expiresInAWeek ? MoreFiltersEnum.expiring : ''
});

const normalizeToApiFilter = (
  values: FilterValues,
  bondInsuranceId?: string
): ApiFilters => {
  const searchType = values.type as 'document' | 'name' | 'address' | undefined;
  const argument = String(values.argument || '').trim().toLowerCase();

  const filter: FilterData = {
    document: '',
    name: '',
    address: '',
    property_document: '',
    property_name: ''
  };

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  const insurerIds = toFilterIdList(values.insurer);
  if (insurerIds) filter.insurer = insurerIds;
  if (values.estate_id) filter.estate_id = String(values.estate_id);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  const analysisStatuses = toFilterIdList(values.analysisStatus)?.filter(
    status => status !== 'Todos'
  );
  if (analysisStatuses?.length) {
    filter.analysisStatus = analysisStatuses;
  }

  if (values.clientType && values.clientType !== 'Todos') {
    filter.clientType = String(values.clientType);
  }

  if (values.accountManager) {
    filter.accountManager = values.accountManager;
  }

  filter.reanalysis = values.moreFilters === MoreFiltersEnum.reanalysis;
  filter.expiresInAWeek = values.moreFilters === MoreFiltersEnum.expiring;
  filter.onlyUnread = values.moreFilters === MoreFiltersEnum.unread;
  filter.onlyManual = values.moreFilters === MoreFiltersEnum.manual;

  if (bondInsuranceId) {
    filter.bondInsuranceId = bondInsuranceId;
  }

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const BondInsurances = ({
  availableBrokersAndEstates,
  user,
  ...props
}: BondInsuranceListProps) => {
  const id = props.match.params.id;
  const location = useLocation();
  const history = useHistory();
  const locationState = location.state as LocationState | undefined;

  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurers: InsurerState[] = availableBrokersAndEstates.bondInsurers.map(
    (insurer: any) => insurer.value
  );

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(locationState, singleEstateId),
    // Seed only from the first navigation state; singleEstateId may arrive with redux.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleEstateId]
  );

  const hasDeepLink = Boolean(
    locationState?.bondInsuranceAnalysisId ||
      locationState?.bondInsuranceId ||
      locationState?.expiresInAWeek ||
      locationState?.analysisStatus
  );

  const [externalBondInsuranceAnalysisId, setExternalBondInsuranceAnalysisId] = useState(
    locationState?.bondInsuranceAnalysisId
  );
  const [bondInsuranceId, setBondInsuranceId] = useState<string | undefined>(
    locationState?.bondInsuranceId
  );
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(hasDeepLink);

  const selectableInsurers = useMemo(
    () =>
      insurers.map(insurer => ({
        value: insurer.id,
        text: insurer.name
      })),
    [insurers]
  );

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const { data: usersData, isLoading: isAccountManagersLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    ).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const statusSelectOptions = useMemo(
    () =>
      selectableStatus
        .filter(option => option.value !== 'Todos')
        .map(option => ({
          value: String(option.value),
          text: String(option.text)
        })),
    []
  );

  const clientTypeSelectOptions = useMemo(
    () =>
      clientTypeOptions
        .filter(option => option.value !== 'Todos')
        .map(option => ({
          value: String(option.value),
          text: String(option.text)
        })),
    []
  );

  const moreFiltersSelectOptions = useMemo(
    () =>
      moreFiltersOptions
        .filter(option => option.value !== MoreFiltersEnum.none)
        .map(option => ({
          value: String(option.value),
          text: String(option.text)
        })),
    []
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'multiSelect',
        name: 'insurer',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(selectableInsurers)
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estate_id',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estate_id)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'multiSelect',
        name: 'analysisStatus',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusSelectOptions)
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estate_id || user?.estate?.id || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagersLoading,
        options: toSelectOptions(accountManagerOptions),
        getDisplayValue: values => {
          const option = accountManagerOptions.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push({
      kind: 'select',
      name: 'clientType',
      label: 'Tipo de Cliente',
      placeholder: 'Selecionar...',
      advanced: true,
      options: toSelectOptions(clientTypeSelectOptions),
      getDisplayValue: values => {
        const option = clientTypeSelectOptions.find(
          item => String(item.value) === String(values.clientType)
        );
        return option ? option.text : undefined;
      }
    });

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'moreFilters',
        label: 'Mais Filtros',
        placeholder: 'Selecionar...',
        advanced: true,
        options: toSelectOptions(moreFiltersSelectOptions),
        getDisplayValue: values => {
          const option = moreFiltersSelectOptions.find(
            item => String(item.value) === String(values.moreFilters)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isEstateUser,
    selectableInsurers,
    estateOptions,
    statusSelectOptions,
    clientTypeSelectOptions,
    moreFiltersSelectOptions,
    accountManagerOptions,
    isAccountManagersLoading,
    user?.estate?.id
  ]);

  const loadTotal = useCallback(async (filter: ApiFilters) => {
    const response = await getBondInsurances(currentPage, {
      ...filter,
      action: 'COUNT',
      isRv: true
    });
    setTotal(response?.count || response?.current || 0);
  }, [currentPage]);

  const getData = async () => {
    try {
      if (id) {
        const bondInsurance = await getBondInsuranceByID(id);

        if (bondInsurance) {
          return {
            data: [bondInsurance],
            current: 1,
            count: 1,
            page: 1
          };
        }
      }

      const filter: ApiFilters = {
        ...appliedFilters,
        status: 'CREATED',
        isRv: true,
        ...(bondInsuranceId ? { bondInsuranceId } : {})
      };

      const response = await getBondInsurances(currentPage, filter);
      response.data = sortBondInsurances(response.data, true);

      if (bondInsuranceId) {
        setBondInsuranceId(undefined);
      }

      if (currentPage === 1) {
        loadTotal(filter);
      }

      return response;
    } catch (e) {
      console.log({ e });
      dangerNotification('Oops...', 'Não foi possível buscar as análises');
    }
  };

  const {
    data: bondInsurances,
    isFetching: isLoading,
    refetch
  } = useQuery(['getData', currentPage, appliedFilters], getData, {
    keepPreviousData: true,
    refetchOnMount: true,
    staleTime: 5000,
    enabled: isEnabledToFetch
  });

  useEffect(() => {
    if (!isBackOffice || id) {
      setIsEnabledToFetch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const state = location.state as LocationState | undefined;
    if (!state?.bondInsuranceId && !state?.analysisStatus && !state?.bondInsuranceAnalysisId) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: {
        ...state,
        bondInsuranceId: undefined,
        analysisStatus: undefined,
        bondInsuranceAnalysisId: undefined
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    setBondInsuranceId(undefined);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setBondInsuranceId(undefined);
    setExternalBondInsuranceAnalysisId(undefined);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const cleanLocation = () => {
    setExternalBondInsuranceAnalysisId(undefined);
    setBondInsuranceId(undefined);
  };

  return (
    <>
      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoading}
        syncToUrl
        urlNamespace="analyses"
        hasExternalActiveFilters={Boolean(bondInsuranceId)}
      />

      <BondInsurancesTable
        bondInsurances={bondInsurances?.data || []}
        isLoading={isLoading}
        currentPage={bondInsurances?.page || 1}
        setCurrentPage={setCurrentPage}
        getData={() => refetch()}
        dataCount={total}
        externalBondInsuranceAnalysisId={externalBondInsuranceAnalysisId}
        cleanIdReferences={cleanLocation}
      />

      <PopupModal
        locationActivation={BannerLocationActivationEnum.BOND_INSURANCE_ANALISYS_LIST}
      />
    </>
  );
};

export default BondInsurances;
